import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import Header from '../components/Header';
import { Helmet } from 'react-helmet-async';
import DevelopmentsMap from '../components/DevelopmentsMap';
import { progressTimelinePath } from '../data/developmentProgressTimelines';

type DevelopmentTab = 'current' | 'planned' | 'recent';

type Development = {
  id: string;
  name: string;
  location: string;
  summary?: string;
  imageSrc?: string;
  readMoreUrl?: string;
  progressUrl?: string;
  liveCamEmbedSrc?: string;
};

const CURRENT: Development[] = [
  {
    id: 'broomfield',
    name: 'Broomfield House',
    location: 'Chelmsford',
    summary: 'Broomfield House will be opening its doors at the end of 2026.',
    imageSrc: '/Broomfield_CGI_Front.jpg',
    readMoreUrl: '/developments/broomfield',
    progressUrl: progressTimelinePath('broomfield'),
    liveCamEmbedSrc:
      'https://dash.evercam.io/v2/widgets?widgetKey=a9b3bd3ab548444b8d5a62206ba8fde4&widgetId=7bbbae81&isWidget=true',
  },
  {
    id: 'southlands',
    name: 'Southlands House',
    location: 'Sittingbourne',
    summary: 'Southlands House will be opening its doors in early 2027.',
    imageSrc: '/Southlands-House-5.jpg',
    readMoreUrl: '/developments/southlands',
    progressUrl: progressTimelinePath('southlands'),
    liveCamEmbedSrc:
      'https://dash.evercam.io/v2/widgets?widgetKey=bcb61e0202264999ba476d599005e8b4&widgetId=bfe3240e&isWidget=true',
  },
  {
    id: 'whitstable-heights',
    name: 'Whitstable Heights',
    location: 'Whitstable',
    summary:
      'Whitstable Heights is a 102-suite acute care home, forming Phase 2 of the Estuary View development in Whitstable.',
    imageSrc: '/Whitstable-heights-CGI.jpg',
    progressUrl: progressTimelinePath('whitstable-heights'),
    liveCamEmbedSrc:
      'https://dash.evercam.io/v2/widgets?widgetKey=d394ceb2982b462cb1ef635725bf9293&widgetId=0774f5e4&isWidget=true',
  },
];

const PLANNED: Development[] = [
  {
    id: 'pembury',
    name: 'Pembury House',
    location: 'Tunbridge Wells',
    imageSrc: '/Pembury_House_Cornford.jpg',
    progressUrl: progressTimelinePath('pembury'),
  },
  {
    id: 'bishopswood',
    name: 'Bishopswood House',
    location: 'Basingstoke',
    imageSrc: '/Developments_Cover_Image.jpg',
  },
];

const RECENT: Development[] = [
  {
    id: 'harpwood',
    name: 'Harpwood House',
    location: 'Sevenoaks',
    imageSrc: '/Reception_Cover.jpg',
    progressUrl: progressTimelinePath('harpwood'),
  },
  {
    id: 'whitstable-phase-1',
    name: 'Whitstable House Phase 1',
    location: 'Whitstable',
    imageSrc: '/whitstable-front-image.jpg',
    progressUrl: progressTimelinePath('whitstable-phase-1'),
  },
  {
    id: 'rodwell',
    name: 'Rodwell House',
    location: 'Addlestone',
    imageSrc: '/Rodwell-Front.jpg',
    progressUrl: progressTimelinePath('rodwell'),
  },
];

const TAB_DATA: Record<DevelopmentTab, Development[]> = {
  current: CURRENT,
  planned: PLANNED,
  recent: RECENT,
};

const DEVELOPMENT_HERO_IMAGES = ['/Developments_Cover_Image.jpg', '/Whitstable-heights-CGI.jpg'];

const TAB_LABELS: { id: DevelopmentTab; label: string }[] = [
  { id: 'current', label: 'Current' },
  { id: 'planned', label: 'Planned' },
  { id: 'recent', label: 'Recent' },
];

type DevelopmentLink = {
  href: string;
  label: string;
  className?: string;
  external?: boolean;
};

function developmentActionLinks(development: Development): DevelopmentLink[] {
  const links: DevelopmentLink[] = [];

  if (development.progressUrl) {
    links.push({
      href: development.progressUrl,
      label: 'Progress timeline',
      className: 'developments-row__timeline',
      external: development.progressUrl.startsWith('http'),
    });
  }

  return links;
}

function DevelopmentRow({
  development,
  onShowOnMap,
}: {
  development: Development;
  onShowOnMap: (id: string) => void;
}) {
  const actionLinks = developmentActionLinks(development);
  const [liveCamOpen, setLiveCamOpen] = useState(false);
  const liveCamEmbedRef = useRef<HTMLDivElement>(null);

  const toggleLiveCam = () => {
    setLiveCamOpen((open) => {
      const next = !open;
      if (next) {
        requestAnimationFrame(() => {
          liveCamEmbedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
      return next;
    });
  };

  return (
    <li className="developments-row">
      <div className="developments-row__image">
        {development.imageSrc ? (
          <img src={development.imageSrc} alt={`${development.name}, ${development.location}`} />
        ) : (
          <div className="developments-row__placeholder" role="img" aria-label={development.name} />
        )}
      </div>
      <div className="developments-row__text">
        <h3>
          {development.name}, {development.location}
        </h3>
        {(development.summary || development.readMoreUrl) && (
          <p>
            {development.summary}
            {development.summary && development.readMoreUrl && ' '}
            {development.readMoreUrl && (
              <>
                Read more about the scheme{' '}
                <Link to={development.readMoreUrl} className="nurse-care-link">
                  here
                </Link>
                .
              </>
            )}
          </p>
        )}
        <p className="developments-row__actions">
          {actionLinks.map((link, index) => (
            <Fragment key={link.label}>
              {index > 0 && <span className="developments-row__sep"> · </span>}
              {link.external ? (
                <a
                  href={link.href}
                  className={`nurse-care-link ${link.className ?? ''}`.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              ) : (
                <Link to={link.href} className={`nurse-care-link ${link.className ?? ''}`.trim()}>
                  {link.label}
                </Link>
              )}
            </Fragment>
          ))}
          {actionLinks.length > 0 && development.liveCamEmbedSrc && (
            <span className="developments-row__sep"> · </span>
          )}
          {development.liveCamEmbedSrc && (
            <button
              type="button"
              className="nurse-care-link developments-row__map-btn"
              aria-expanded={liveCamOpen}
              aria-controls={`developments-live-cam-${development.id}`}
              onClick={toggleLiveCam}
            >
              {liveCamOpen ? 'Hide live cam' : 'Construction Live Cam'}
            </button>
          )}
          {(actionLinks.length > 0 || development.liveCamEmbedSrc) && (
            <span className="developments-row__sep"> · </span>
          )}
          <button
            type="button"
            className="nurse-care-link developments-row__map-btn"
            onClick={() => onShowOnMap(development.id)}
          >
            Show on Map
          </button>
        </p>
        {development.liveCamEmbedSrc && liveCamOpen && (
          <div
            ref={liveCamEmbedRef}
            id={`developments-live-cam-${development.id}`}
            className="developments-live-cam"
          >
            <iframe
              src={development.liveCamEmbedSrc}
              title={`${development.name} construction live camera`}
              allow="fullscreen"
              sandbox="allow-scripts allow-same-origin"
              className="developments-live-cam__iframe"
            />
          </div>
        )}
      </div>
    </li>
  );
}

function Developments() {
  const [activeTab, setActiveTab] = useState<DevelopmentTab>('current');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fadeClass, setFadeClass] = useState('fade-in');
  const [selectedMapId, setSelectedMapId] = useState<string | null>(null);
  const mapSectionRef = useRef<HTMLDivElement>(null);

  const handleShowOnMap = useCallback((id: string) => {
    setSelectedMapId(id);
    mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (DEVELOPMENT_HERO_IMAGES.length <= 1) return;

    const interval = setInterval(() => {
      setFadeClass('fade-out');
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % DEVELOPMENT_HERO_IMAGES.length);
        setFadeClass('fade-in');
      }, 500);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const activeDevelopments = TAB_DATA[activeTab];

  return (
    <>
      <Helmet>
        <title>Developments | Graham Care</title>
        <meta
          name="description"
          content="Explore Graham Care's £100+ million development programme — purpose-built care homes and upcoming schemes across the UK."
        />
      </Helmet>

      <div className="page developments-page-wrap">
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <Header />

        <main id="main-content">
          <section className="hero developments-hero" aria-label="Our Developments">
            <div
              className={`hero__image ${fadeClass}`}
              style={{ backgroundImage: `url('${DEVELOPMENT_HERO_IMAGES[currentImageIndex]}')` }}
            />
            <div className="hero__content">
              <h1 className="hero__title">
                <span className="hero__title-main developments-hero__title">Our Developments</span>
              </h1>
            </div>
          </section>

          <section className="content-section developments-body">
            <div className="developments-container">
              <p className="developments-lead">
                Graham Care progressing with £100+ million Development Programme.
              </p>

              <div className="developments-content">
                <p>
                  Our strategy is to acquire development sites and retain the freehold while securing planning
                  consent for new care schemes. Over the years, we have built a strong pipeline of sites—many of
                  which now benefit from full planning approval and are ready for development.
                </p>
                <p>
                  Our developments are purpose-built to the highest specification, with care suites that are
                  significantly larger than the industry standard. Each home is designed to meet the needs of the
                  entire local population, ensuring that the socio-economic profile of our residents reflects the
                  community around the home itself.
                </p>
                <p>
                  Our homes provide for a wide range of needs: from short-term stays for rehabilitation and
                  recovery to long-term residency for those seeking a true home for life. The high specification
                  includes specialist equipment to support residents with high levels of physical frailty, as well
                  as dedicated areas for people living with dementia and other organic mental health conditions. Our
                  latest projects also feature rehabilitation facilities such as hydrotherapy pools, gyms, and
                  dedicated dining spaces, helping people recover post-operatively before returning home.
                </p>
                <p>
                  Every scheme is developed on a large scale, enabling us to bring together a portfolio of care
                  specialisms under one roof. This not only meets the diverse needs of local people but also
                  provides valuable capacity to relieve pressure on nearby NHS acute facilities.
                </p>
                <p>
                  With the growing demand for complex care, our buildings are designed not just to an exceptional
                  physical standard but also to be fully technologically enabled. This allows residents to be
                  discreetly monitored, so care can be delivered safely and effectively, with minimal intrusion
                  and highly personalised outcomes.
                </p>
                <p>
                  As part of our ongoing growth strategy, we typically progress two to three major projects at any
                  one time. Details of our current and upcoming schemes can be explored below.
                </p>
              </div>

              <div className="developments-picker">
                <div className="developments-tabs" role="tablist" aria-label="Development categories">
                  {TAB_LABELS.map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      id={`developments-tab-${id}`}
                      aria-selected={activeTab === id}
                      aria-controls={`developments-panel-${id}`}
                      className={`developments-tab ${activeTab === id ? 'developments-tab--active' : ''}`}
                      onClick={() => setActiveTab(id)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div
                  id={`developments-panel-${activeTab}`}
                  role="tabpanel"
                  aria-labelledby={`developments-tab-${activeTab}`}
                  className="developments-panel"
                >
                  {activeDevelopments.length > 0 ? (
                    <ul className="developments-rows">
                      {activeDevelopments.map((development) => (
                        <DevelopmentRow
                          key={development.id}
                          development={development}
                          onShowOnMap={handleShowOnMap}
                        />
                      ))}
                    </ul>
                  ) : (
                    <p className="developments-empty">No developments in this category.</p>
                  )}
                </div>
              </div>

              <div ref={mapSectionRef}>
                <DevelopmentsMap selectedId={selectedMapId} />
              </div>
            </div>
          </section>
        </main>

        <footer className="footer">
          <section className="footer__enquiry">
            <div className="footer__enquiry-container">
              <Link to="/contact-us" className="footer__enquiry-link">
                Enquire about Graham Care <span className="footer__arrow">▶</span>
              </Link>
            </div>
          </section>
          <div className="footer__container">
            <section className="contact-section">
              <div className="contact-section__content">
                <p>
                  For enquiries about our homes, please{' '}
                  <Link to="/contact-us" className="nurse-care-link">
                    contact us
                  </Link>
                  .
                </p>
                <p>Copyright © Graham Care Ltd</p>
              </div>
            </section>
          </div>
        </footer>
      </div>
    </>
  );
}

export default Developments;
